import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Medal, Award, Sparkles, Clock, LogOut } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useAppDialog } from "../components/AppDialog";
import Coin from "../components/Coin";
import EditInfoCard from "../components/EditInfoCard";
import { GAMES } from "../data/games";
import { getVisitorId } from "../utils/analytics";

const BASE = process.env.REACT_APP_BACKEND_URL;

const fmtHours = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h}h ${m}m`;
};
import { STICKERS } from "../data/stickers";
import { BRACKETS, bracketFor, nextBracket, computeGlobalRank } from "../data/leaderboard";
import usePageMeta from "../hooks/usePageMeta";

const Profile = () => {
  const {
    name,
    coins,
    avatar,
    gameStats,
    gamesWon,
    stickers,
    orders,
    streakDays,
    totalPlaySeconds,
    signOut,
    hydrateFromServer,
  } = useStore();
  const nav = useNavigate();
  const { confirm } = useAppDialog();

  // Hydrate the persistent selections (avatar / display name / dob) from the
  // canonical visitor row on mount so a device switch or auto-merge doesn't
  // leave the profile showing local defaults. Runs once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${BASE}/api/visitor/me?visitor_id=${encodeURIComponent(getVisitorId())}`,
          { credentials: "include" },
        );
        if (!r.ok || cancelled) return;
        const me = await r.json();
        hydrateFromServer(me);
      } catch { /* best effort */ }
    })();
    return () => { cancelled = true; };
  }, [hydrateFromServer]);
  const doSignOut = async () => {
    const ok = await confirm({
      title: "Sign out?",
      message:
        "You'll return to the welcome screen. Your local coins, cart, wishlist and progress on this device will be cleared.",
      tone: "warning",
      confirmLabel: "Sign out",
    });
    if (!ok) return;
    signOut();
    nav("/welcome", { replace: true });
  };
  usePageMeta(
    "My Profile · 67Cafe",
    "See your scores, game wins and where you rank against other pretend shoppers today."
  );

  // Total win count across all games + a bonus per unique game won.
  const totalWins = Object.values(gamesWon).reduce((s, n) => s + n, 0);
  const uniqueGames = Object.keys(gamesWon).length;
  // Score used for leaderboard: coins earned in games + a small bonus per win.
  const playerScore = gameStats.coinsEarned + totalWins * 15;
  const bracket = bracketFor(playerScore);
  const next = nextBracket(playerScore);
  const nextGap = next ? Math.max(0, next.min - playerScore) : 0;
  const nextProgress = next
    ? Math.min(100, Math.round(((playerScore - bracket.min) / (next.min - bracket.min)) * 100))
    : 100;
  const { rank: localRank, total: localTotal, rows } = computeGlobalRank(playerScore);
  // Fetch REAL leaderboard rank so the numbers here match /leaderboard.
  const [realRank, setRealRank] = useState(null);
  const [realTotal, setRealTotal] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const vid = encodeURIComponent(getVisitorId());
    fetch(`${BASE}/api/leaderboard?sort=playtime&limit=1&visitor_id=${vid}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        // Only publish the API rank+total as a PAIR — otherwise a null `me`
        // combined with a real total_ranked produces nonsense like #951/38.
        if (j && j.me && typeof j.me.rank === "number" && typeof j.total_ranked === "number") {
          setRealRank(j.me.rank);
          setRealTotal(j.total_ranked);
        }
      })
      .catch(() => { /* fall back to local rank */ });
    return () => { cancelled = true; };
  }, []);
  const rank = realRank ?? localRank;
  const total = realTotal ?? localTotal;
  const percentile = Math.min(100, Math.max(1, Math.round((rank / total) * 100)));

  // Top played games (sorted by wins).
  const wonGames = GAMES.filter((g) => gamesWon[g.id])
    .map((g) => ({ ...g, wins: gamesWon[g.id] }))
    .sort((a, b) => b.wins - a.wins);

  return (
    <div
      className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8"
      data-testid="profile-page"
    >
      <Link
        to="/shop"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="profile-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={3} /> Back
      </Link>

      <EditInfoCard />

      {/* Total playtime + leaderboard link */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div
          className="chunky-card p-4 flex items-center gap-4"
          style={{ background: "#EAF6FF" }}
          data-testid="profile-total-playtime"
        >
          <div
            className="rounded-full flex items-center justify-center shrink-0"
            style={{
              width: 56, height: 56, background: "#5F27CD", color: "#fff",
              border: "3px solid #2b2b2b", boxShadow: "0 4px 0 #2b2b2b",
            }}
          >
            <Clock size={26} strokeWidth={3} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-display uppercase" style={{ color: "#484848" }}>
              Total time played
            </div>
            <div
              className="font-display text-3xl sm:text-4xl leading-none"
              data-testid="profile-total-playtime-value"
              style={{ color: "#2b2b2b" }}
            >
              {fmtHours(totalPlaySeconds)}
            </div>
          </div>
        </div>

        <Link
          to="/leaderboard"
          className="chunky-card p-4 flex items-center gap-4 transition-transform hover:-translate-y-0.5"
          style={{ background: "#FFB400", textDecoration: "none", color: "#2b2b2b" }}
          data-testid="profile-leaderboard-link"
        >
          <div
            className="rounded-full flex items-center justify-center shrink-0"
            style={{
              width: 56, height: 56, background: "#fff",
              border: "3px solid #2b2b2b", boxShadow: "0 4px 0 #2b2b2b",
            }}
          >
            <Trophy size={26} strokeWidth={3} color="#FFB400" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-display uppercase" style={{ color: "#4a3200" }}>
              Real leaderboard
            </div>
            <div className="font-display text-2xl leading-tight">
              See where you stand
            </div>
          </div>
        </Link>
      </div>

      {/* Bracket hero */}
      <div
        className="chunky-card p-5 sm:p-6 mb-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${bracket.color}22 0%, #FFF9E6 90%)`,
        }}
        data-testid="profile-hero"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div
            className="rounded-full flex items-center justify-center shrink-0"
            style={{
              width: 88,
              height: 88,
              background: bracket.color,
              border: "4px solid #2b2b2b",
              boxShadow: "0 5px 0 #2b2b2b",
            }}
            data-testid="profile-bracket-badge"
          >
            <span className="text-4xl bob" aria-hidden="true">
              {bracket.emoji}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="inline-flex items-center gap-2 text-sm font-display"
              style={{ color: bracket.color }}
            >
              <Sparkles size={16} strokeWidth={3} />
              {bracket.name} bracket
            </div>
            <h1
              className="font-display text-3xl sm:text-4xl leading-tight mt-1"
              data-testid="profile-name"
            >
              {name || "You"}&#39;s profile
            </h1>
            <p className="text-sm sm:text-base mt-1" style={{ color: "#484848" }}>
              {bracket.blurb}
            </p>
          </div>
          <div
            className="chunky-card px-4 py-3 text-center"
            style={{ background: "#fff", boxShadow: "0 4px 0 #2b2b2b" }}
            data-testid="profile-rank-summary"
          >
            <div className="text-xs font-display" style={{ color: "#484848" }}>
              Global rank today
            </div>
            <div className="font-display text-2xl leading-tight">
              #<span data-testid="profile-rank">{rank}</span>
              <span className="text-base" style={{ color: "#484848" }}>
                {" "}
                / <span data-testid="profile-total">{total}</span>
              </span>
            </div>
            <div className="text-xs mt-1" style={{ color: "#00A699" }}>
              Top <strong data-testid="profile-percentile">{percentile}%</strong>
            </div>
          </div>
        </div>

        {next ? (
          <div className="mt-5" data-testid="profile-next-bracket">
            <div className="flex items-center justify-between text-sm font-display">
              <span>
                Next: {next.emoji} {next.name}
              </span>
              <span style={{ color: "#484848" }}>{nextGap} more to level up</span>
            </div>
            <div
              className="mt-1 rounded-full overflow-hidden"
              style={{
                background: "#fff",
                border: "3px solid #2b2b2b",
                height: 18,
              }}
            >
              <div
                data-testid="profile-next-progress"
                style={{
                  width: `${nextProgress}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${bracket.color}, ${next.color})`,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>
        ) : (
          <div
            className="mt-5 text-center font-display text-base"
            style={{ color: "#FF5A5F" }}
            data-testid="profile-max-bracket"
          >
            Top of the leaderboard bracket! 👑
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div
          className="chunky-card p-4 text-center"
          style={{ background: "#FFE0F5" }}
          data-testid="profile-stat-coins"
        >
          <Coin size={28} />
          <div className="font-display text-2xl mt-1">{gameStats.coinsEarned}</div>
          <div className="text-xs" style={{ color: "#484848" }}>
            Coins earned
          </div>
        </div>
        <div
          className="chunky-card p-4 text-center"
          style={{ background: "#E5F6FF" }}
          data-testid="profile-stat-wallet"
        >
          <div className="text-3xl">💰</div>
          <div className="font-display text-2xl mt-1">{coins}</div>
          <div className="text-xs" style={{ color: "#484848" }}>
            Wallet
          </div>
        </div>
        <div
          className="chunky-card p-4 text-center"
          style={{ background: "#FFF3D6" }}
          data-testid="profile-stat-wins"
        >
          <Trophy size={26} strokeWidth={3} style={{ color: "#FFB400" }} />
          <div className="font-display text-2xl mt-1">{totalWins}</div>
          <div className="text-xs" style={{ color: "#484848" }}>
            Game wins · {uniqueGames} games
          </div>
        </div>
        <div
          className="chunky-card p-4 text-center"
          style={{ background: "#E9FFE6" }}
          data-testid="profile-stat-streak"
        >
          <div className="text-3xl">🔥</div>
          <div className="font-display text-2xl mt-1">{streakDays}</div>
          <div className="text-xs" style={{ color: "#484848" }}>
            Day streak · {orders.length} orders · {stickers.length}/{STICKERS.length}{" "}
            stickers
          </div>
        </div>
      </div>

      {/* Leaderboard slice */}
      <div className="chunky-card p-4 sm:p-5 mb-6" data-testid="profile-leaderboard">
        <div className="flex items-center gap-2 mb-3">
          <Medal size={22} strokeWidth={3} style={{ color: "#FFB400" }} />
          <h2 className="font-display text-xl sm:text-2xl">Where you rank</h2>
        </div>
        <p className="text-xs mb-3" style={{ color: "#484848" }}>
          Anonymous pretend players — names are hidden. Refreshes daily.
        </p>
        <ol className="space-y-2">
          {rows.map((r) => {
            // Match the /leaderboard row visual: chunky-card + medal-pill for
            // top-3, cream-tinted numeric pill after that.
            const rc =
              r.position === 1 ? { bg: "#FFB400", label: "🥇" }
              : r.position === 2 ? { bg: "#C0C7CE", label: "🥈" }
              : r.position === 3 ? { bg: "#D89365", label: "🥉" }
              : { bg: "#FFF9E6", label: `#${r.position}` };
            // Synthetic avatar per row so the visual feels alive without
            // pulling real avatars for pretend players.
            const AV = ["🐻", "🦊", "🐼", "🐨", "🐯", "🦁", "🐸", "🐵", "🦄", "🐰"];
            const avatarEmoji = r.isYou
              ? (avatar || "🦊")
              : AV[(r.position * 7) % AV.length];
            return (
              <li
                key={r.position}
                data-testid={`leaderboard-row-${r.position}`}
                data-you={r.isYou ? "true" : "false"}
                className="chunky-card p-3 flex items-center gap-3"
                style={r.isYou ? { background: "#EAF6FF" } : {}}
              >
                <div
                  className="rounded-full flex items-center justify-center font-display shrink-0"
                  style={{
                    width: 44, height: 44,
                    fontSize: r.position <= 3 ? 22 : 16,
                    lineHeight: 1,
                    background: rc.bg,
                    border: "3px solid #2b2b2b",
                    boxShadow: "0 3px 0 #2b2b2b",
                  }}
                >
                  {rc.label}
                </div>
                <div
                  className="rounded-full flex items-center justify-center shrink-0"
                  style={{
                    width: 40, height: 40, fontSize: 22, background: "#fff",
                    border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b",
                  }}
                  aria-hidden="true"
                >
                  {avatarEmoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-tight truncate">
                    {r.isYou ? (
                      <span data-testid="leaderboard-you">
                        You ({name || "Kid"})
                        <span
                          className="ml-2 rounded-full px-2 py-0.5 text-xs align-middle"
                          style={{ background: "#54A0FF", color: "#fff", border: "2px solid #2b2b2b" }}
                        >
                          YOU
                        </span>
                      </span>
                    ) : (
                      <>Player #{100 + ((r.position * 37) % 900)}</>
                    )}
                  </div>
                </div>
                <div className="inline-flex items-center gap-1 font-display text-base sm:text-lg shrink-0">
                  <Coin size={16} />
                  {r.score}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Game wins breakdown */}
      <div className="chunky-card p-4 sm:p-5" data-testid="profile-wins-breakdown">
        <div className="flex items-center gap-2 mb-3">
          <Award size={22} strokeWidth={3} style={{ color: "#F368E0" }} />
          <h2 className="font-display text-xl sm:text-2xl">Your game wins</h2>
        </div>
        {wonGames.length === 0 ? (
          <p className="text-sm" style={{ color: "#484848" }}>
            No wins yet — head to <Link to="/games" className="underline" style={{ color: "#5F27CD" }}>Games</Link> and earn some coins!
          </p>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {wonGames.map((g) => (
              <li
                key={g.id}
                data-testid={`profile-game-${g.id}`}
                className="chunky-card p-3 flex items-center gap-3"
                style={{ background: g.color + "22" }}
              >
                <div
                  className="rounded-full flex items-center justify-center shrink-0"
                  style={{
                    width: 44,
                    height: 44,
                    background: g.color,
                    border: "3px solid #2b2b2b",
                    boxShadow: "0 3px 0 #2b2b2b",
                  }}
                >
                  <span className="text-xl" aria-hidden="true">
                    {g.emoji}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="font-display text-sm leading-tight truncate">
                    {g.name}
                  </div>
                  <div
                    className="text-xs inline-flex items-center gap-1"
                    style={{ color: "#00A699" }}
                  >
                    <Trophy size={12} strokeWidth={3} /> {g.wins} win
                    {g.wins === 1 ? "" : "s"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bracket ladder */}
      <div
        className="chunky-card p-4 sm:p-5 mt-6"
        data-testid="profile-bracket-ladder"
      >
        <h2 className="font-display text-lg sm:text-xl mb-3">Bracket ladder</h2>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {BRACKETS.map((b) => {
            const active = b.id === bracket.id;
            return (
              <div
                key={b.id}
                data-testid={`bracket-${b.id}`}
                className="rounded-2xl px-3 py-2 flex items-center gap-2"
                style={{
                  background: active ? b.color : "#fff",
                  color: active ? "#fff" : "#484848",
                  border: "3px solid #2b2b2b",
                  boxShadow: active ? `0 4px 0 #2b2b2b` : `0 3px 0 #2b2b2b`,
                }}
              >
                <span className="text-xl" aria-hidden="true">
                  {b.emoji}
                </span>
                <div className="font-display text-sm leading-tight">
                  <div>{b.name}</div>
                  <div className="text-[10px] opacity-80">
                    {b.min}+ pts
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="chunky-card p-4 sm:p-5 mt-6 flex items-center justify-between gap-3 flex-wrap"
        data-testid="profile-signout-card"
        style={{ background: "#FFF" }}
      >
        <div>
          <div className="font-display text-base">Signed in as {name || "guest"}</div>
          <div className="text-xs" style={{ color: "#484848" }}>
            Sign out to clear this device&#39;s coins, cart and stickers and return
            to the welcome screen.
          </div>
        </div>
        <button
          type="button"
          onClick={doSignOut}
          data-testid="profile-signout-btn"
          className="btn-3d btn-sm btn-danger"
        >
          <LogOut size={14} strokeWidth={3} />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default Profile;
